import React, { ReactElement, useState, useEffect, useRef } from 'react';
import { Flex } from 'rebass/styled-components';
import { useRouteMatch, useHistory, useParams } from 'react-router-dom';

import AddEditSection from './AddEditSection';
import AddGlobalSection from './AddGlobalSection';
import AddEditQuestion from './AddEditQuestion';
import SectionList from './SectionList';
import { formatQuestionOptionsData } from './AddEditQuestion/formatQuestionData';
import CopySection from './CopySection';

import { MainContent, PageHeader, FormInput, Loader } from 'ui/patterns';

import {
  useDocumentTitle,
  useGetFormDetails,
  useGetAccountInfo,
  useSaveForm,
  useCopySection,
  useCopyQuestion,
} from 'hooks';
import usePrompt from 'hooks/usePrompt';

import * as Icon from 'assets/icons';

import { confirmAlert } from 'utils/confirm-alert';
import { showErrorToast, showSuccessToast } from 'utils/showToast';
import { useStateCallback } from './genericFn';

import { SectionTypeInput } from 'types/forms';
import { Questions, QuestionTypeInput } from 'types/questions';
import { ErrorModal } from 'types/modalTypes';
import { AddEditFormType } from './addEditFormTypes';

const empty = {
  accountInfoReturn: undefined,
};

const usePrevious = <T extends unknown>(value: T): T | undefined => {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export default function AddEditForm({
  ...StyleProps
}: AddEditFormType): ReactElement {
  const [showNewSection, setNewSection] = useState<boolean>(false);
  const [showGlobalSection, setGlobalSection] = useState<boolean>(false);
  const [showQuestionPanel, setQuestionPanel] = useState<boolean>(false);
  const [sections, setSections] = useStateCallback<Array<SectionTypeInput>>([]);
  const [currentSection, setCurrentSection] =
    useState<SectionTypeInput>(undefined);
  const [formNotes, setFormNotes] = useState<string>('');
  const [isAdd, setIsAdd] = useStateCallback<boolean>(false);
  const [currentQuestion, setCurrentQuestion] = useState({});
  const [selectedQuestionType, setQuestionType] = useState({});
  const [showCopyQuestionPanel, setShowCopyQuestion] = useState<number>(0);
  const [showCopySection, setCopySection] = useState<boolean>(false);
  const [expandAll, setExpandAll] = useState<Record<number, boolean>>({});
  const [url, setUrl] = useState<string>('');

  const history = useHistory();
  const { accountId: passedId, formId: passedFormId } = useParams<{
    accountId: string;
    formId: string;
  }>();

  const match = useRouteMatch(`/home/accounts/edit/${passedId}/AddForm`);
  const formId = parseInt(passedFormId);
  const accountId = parseInt(passedId);

  useDocumentTitle(`${match ? 'Add Form' : 'Edit Form'}`, true);

  const { mutate: copySectionApi, isLoading: copySectionLoading } =
    useCopySection();

  const { mutate: copyQuestionApi, isLoading: copyQnLoading } =
    useCopyQuestion();

  const { data: formDetails, isLoading: isFormLoading } = useGetFormDetails(
    accountId,
    formId,
  );
  const { data: accountInfo, isLoading: isAccountLoading } =
    useGetAccountInfo(accountId);

  const { mutate: saveForm, isLoading: isSaveFormLoading } = useSaveForm();

  const { accountInfoReturn } = accountInfo || empty;

  const isLoading =
    isFormLoading ||
    isAccountLoading ||
    isSaveFormLoading ||
    copySectionLoading ||
    copyQnLoading;

  const accountNumber = accountInfoReturn
    ? `${accountInfoReturn?.Data.CID}${'-'}${
        accountInfoReturn?.Data.SID
      }${'-'}${accountInfoReturn?.Data.PID}`
    : '';

  useEffect(() => {
    if (formDetails && formDetails.Data) setFormNotes(formDetails.Data.Notes);
  }, [formDetails]);

  useEffect(() => {
    if (
      formDetails &&
      formDetails.Data &&
      formDetails.Data.Sections.length > 0
    ) {
      setSections([...formDetails?.Data.Sections]);
    }
  }, [formDetails, setSections]);

  const prevFormDetails = usePrevious({ formDetails });

  useEffect(() => {
    if (!prevFormDetails?.formDetails) {
      const expandedList: Record<number, boolean> = {};
      const sections = formDetails?.Data?.Sections;
      sections &&
        sections
          .filter((item: any) => item.$type === 'Section')
          .forEach((item: any) => {
            expandedList[item.Id] = true;
          });
      setExpandAll({ ...expandedList });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDetails]);

  function switchTab(tabId: string, formId?: string | number) {
    history.push(
      `/home/accounts/edit/${accountId}/${tabId}${formId ? `/${formId}` : ''}`,
    );
  }

  usePrompt(
    isAdd,
    'Are you sure you want to leave? Changes that you made may not be saved.',
  );

  function openCloseSection(id: number) {
    const expandedState = { ...expandAll };
    expandedState[id] = expandedState[id] ? false : true;
    setExpandAll({ ...expandedState });
  }

  function allSectionExpandCollapse() {
    const showCollapsed = Object.values(expandAll).find(item => item === true);
    const expandedList: Record<number, boolean> = {};
    sections &&
      sections
        .filter(item => item.$type === 'Section')
        .forEach(item => {
          expandedList[item.Id] = showCollapsed ? false : true;
        });
    setExpandAll({ ...expandedList });
  }

  const toggleNewSection = () => {
    setNewSection(!showNewSection);
    setCurrentSection(undefined);
  };

  const toggleCopyQuestionPanel = (sectionId?: number) => {
    setShowCopyQuestion(sectionId ? sectionId : 0);
  };

  const toggleCopySectionPanel = () => {
    setCopySection(!showCopySection);
  };

  const toggleGlobalSection = () => {
    setGlobalSection(false);
  };

  const toggleQuestionPanel = () => {
    setQuestionPanel(!showQuestionPanel);
    setCurrentQuestion({});
    setQuestionType({});
    setCurrentSection(undefined);
  };

  const handleAddEditSections = (sectionItem: SectionTypeInput) => {
    const allSections = [...sections];
    const index =
      allSections && allSections.length > 0
        ? allSections.findIndex(item => item.Id === sectionItem.Id)
        : -1;
    if (index === -1) {
      const newSection = {
        ...sectionItem,
        Order: sectionItem.Order || allSections.length + 1,
      };
      allSections && allSections.push(newSection);
    } else if (index >= 0) {
      allSections && allSections.splice(index, 1, sectionItem);
    }
    setSections([...allSections]);
    setIsAdd(true);
  };

  const handleCurrentSection = (sectionItem: SectionTypeInput) => {
    if (sectionItem) {
      setCurrentSection(sectionItem);
    }
  };

  const handleDeleteSection = (sectionItem: SectionTypeInput) => {
    const updatedSections = (sections ?? [])
      .filter(section => section.Id !== sectionItem.Id)
      .map(section => ({
        ...section,
        Questions: (section.Questions ?? []).map((question: any) => ({
          ...question,
          QuestionOptions: (question.QuestionOptions ?? []).map(
            (option: any) => ({
              ...option,
              DependentSections: (option.DependentSections ?? []).filter(
                (dependentSection: any) =>
                  dependentSection.DependentSectionId !== sectionItem.Id,
              ),
            }),
          ),
        })),
      }));
    setSections(updatedSections);
    setIsAdd(true);
  };

  const handleAddEditGlobalSection = (sectionItem: SectionTypeInput) => {
    const allSections = [...sections];
    const index =
      allSections && allSections.length > 0
        ? allSections.findIndex(item => item.SectionId === sectionItem.Id)
        : -1;
    if (sectionItem && index === -1) {
      allSections.push({
        $type: 'GlobalSectionReference',
        Id: new Date().getTime(),
        IsActive: true,
        SectionId: sectionItem?.Id,
        Title: sectionItem?.Title,
        Questions: [],
        Type: 0,
      });

      setSections([...allSections]);
      setIsAdd(true);
    } else {
      confirmAlert({
        message: 'Form may not have duplicate global sections.',
        buttons: [
          {
            label: 'Ok',
            onClick: () => {
              //
            },
            className: 'pr-btn-primary',
          },
        ],
      });
    }
  };

  const handleAddEditQuestion = (
    questionParams: QuestionTypeInput,
    currentSection: SectionTypeInput,
    cb?: (newSections: Array<SectionTypeInput>, questionParams: any) => void,
  ) => {
    if (
      questionParams.QuestionTypeOption.$type === 'LookupQuestionTypeOption'
    ) {
      questionParams.QuestionTypeOption = {
        ...questionParams.QuestionTypeOption,
        CustomLookupTableId: questionParams.QuestionTypeOption.CustomLookupId,
        CustomLookupId: null,
      };
    }
    const allQuestions = [...currentSection?.Questions];
    const index =
      allQuestions && allQuestions.length > 0
        ? allQuestions.findIndex(
            (item: QuestionTypeInput) =>
              item.QuestionTemplateId === questionParams?.QuestionTemplateId,
          )
        : -1;

    if (index === -1) {
      const newQuestion = {
        ...questionParams,
        Order: questionParams.Order || allQuestions.length + 1,
      };
      allQuestions && allQuestions.push(newQuestion);
    } else if (index >= 0) {
      allQuestions && allQuestions.splice(index, 1, questionParams);
    }

    const updatedSections = sections.map(item => {
      if (item.Id === currentSection.Id) {
        return {
          ...item,
          Questions: allQuestions,
        };
      } else
        return {
          ...item,
        };
    });

    if (Object.keys(currentQuestion).length !== 0 || index === -1) {
      setSections([...updatedSections], newSections => {
        if (cb) {
          setIsAdd(false);
          cb && cb(newSections, questionParams);
        } else {
          setIsAdd(true);
          toggleQuestionPanel();
          setQuestionType({});
        }
      });
    } else {
      confirmAlert({
        message: 'This question has already been added to this section.',
        buttons: [
          {
            label: 'Ok',
            onClick: () => {
              //
            },
            className: 'pr-btn-primary',
          },
        ],
      });
      setQuestionType({});
    }
  };

  const handleCurrentQuestion = (questionItem: any) => {
    const questionType =
      questionItem?.QuestionTemplate?.QuestionType?.Description;

    const questionTypeOption = {
      ...questionItem?.QuestionTypeOption,
      FieldNames:
        questionItem?.QuestionTemplate?.QuestionTypeOption?.FieldNames,
    };

    const questionTemplateOptions = [
      ...questionItem.QuestionTemplate.QuestionTemplateOptions,
    ];

    const receivedQuestionParams = [...questionItem.QuestionOptions];

    const formatQuestionOptions = formatQuestionOptionsData(
      questionTemplateOptions,
      receivedQuestionParams,
    );
    let associationId = 0;
    if (questionItem?.PersonAssociation === 0) associationId = 1;
    else if (questionItem?.PersonAssociation === 1) associationId = 2;

    if (questionItem) {
      const currentQuestionParams = {
        Description: questionItem?.QuestionTemplate?.Description,
        FieldLabel: questionItem?.FieldLabel,
        FieldName: questionItem?.QuestionTemplate?.FieldName,
        Id: questionItem?.Id,
        IsDisabled: questionItem?.QuestionTemplate?.IsDisabled,
        IsMultiValuesAllowed: questionItem?.IsMultiValuesAllowed,
        Order: questionItem?.Order,
        PersonAssociation: associationId,
        QuestionDetail: questionItem?.QuestionDetail,
        QuestionTemplateId: questionItem?.QuestionTemplateId,
        QuestionTemplateOptions: formatQuestionOptions,
        QuestionType: questionItem?.QuestionTemplate?.QuestionType,
        QuestionTypeId: questionItem?.QuestionTemplate?.QuestionTypeId,
        QuestionTypeOption:
          questionType === 'Lookup'
            ? questionTypeOption
            : questionItem?.QuestionTypeOption,
        SectionId: questionItem?.SectionId,
        IsNeverSentToAccount:
          questionItem?.QuestionTypeOption?.IsNeverSentToAccount,
        IsNotSentToAccountIfNoAnswer:
          questionItem?.QuestionTypeOption?.IsNotSentToAccountIfNoAnswer,
        IsRequired: questionItem?.QuestionTypeOption?.IsRequired,
        IsFlagged: questionItem?.IsFlagged ?? false,
        OriginalFieldLabel: questionItem?.QuestionTemplate?.FieldLabel,
      };
      setQuestionType(currentQuestionParams);
      setCurrentQuestion(questionItem);
    }
  };

  const handleDeleteQuestion = (
    questionItem: QuestionTypeInput,
    sectionItem: SectionTypeInput,
  ) => {
    const updatedSections = sections.map(section => {
      // Remove the actual question from its section
      const questions =
        section.Id === sectionItem.Id
          ? section.Questions.filter(
              (q: any) =>
                !(
                  q.Id === questionItem.Id &&
                  q.QuestionTemplateId === questionItem.QuestionTemplateId
                ),
            )
          : section.Questions;

      // Remove the deleted question from all dependencies
      const updatedQuestions = (questions ?? []).map((question: any) => ({
        ...question,
        QuestionOptions: (question.QuestionOptions ?? []).map(
          (option: any) => ({
            ...option,
            DependentQuestions: (option.DependentQuestions ?? []).filter(
              (dependentQuestion: any) =>
                dependentQuestion.DependentQuestionId !== questionItem.Id,
            ),
          }),
        ),
      }));

      return {
        ...section,
        Questions: updatedQuestions,
      };
    });
    setSections(updatedSections);
    setIsAdd(true);
  };

  function formatDataToSave(newSections?: Array<SectionTypeInput>) {
    const allSections = newSections ? [...newSections] : [...sections];

    allSections.forEach(item => {
      delete item.CallType;
    });

    const sectionsToSave =
      allSections &&
      allSections.map((item, sectionIndex) => {
        const baseSection = {
          ...item,
          Order: sectionIndex + 1,
        };
        if (item.Type === 0) {
          return {
            ...baseSection,
            Id: 0,
          };
        } else {
          return baseSection;
        }
      });

    const saveSections =
      sectionsToSave &&
      sectionsToSave.map(item => {
        const Questions = item.Questions?.map(
          (questionItem: QuestionTypeInput, questionIndex: number) => {
            const baseQuestion = {
              ...questionItem,
              Order: questionIndex + 1,
              QuestionTypeOption: {
                ...questionItem.QuestionTypeOption,
              },
            };

            return baseQuestion;
          },
        );

        return {
          ...item,
          Questions,
        };
      });

    return saveSections;
  }

  function handleSaveForm(
    sectionItem: SectionTypeInput,
    callback?: (newSectionId?: number) => void,
  ) {
    setIsAdd(false);
    const saveSections = formatDataToSave();
    if (formDetails === undefined) {
      saveSections.forEach((section: any) => {
        section.Questions.forEach((question: any) => {
          delete question.QuestionTemplate;
        });
      });
    }

    const oldSections = [...sections];
    if (accountInfoReturn)
      saveForm(
        {
          AccountId: accountId,
          CID: accountInfoReturn.Data.CID,
          Id: formDetails && formDetails.Data ? formDetails.Data.Id : 0,
          LastModified:
            formDetails && formDetails.Data
              ? formDetails.Data.LastModified
              : null,
          LastModifiedBy:
            formDetails && formDetails.Data
              ? formDetails.Data.LastModifiedBy
              : '',
          Notes: formNotes,
          PID: accountInfoReturn?.Data?.PID,
          SID: accountInfoReturn?.Data?.SID,
          Sections: saveSections,
          SurveyStatusId:
            formDetails && formDetails.Data
              ? formDetails.Data.SurveyStatusId
              : 1,
        },
        {
          onSuccess: (mutationResult: any) => {
            if (mutationResult?.Data?.Id) {
              switchTab && switchTab('EditForm', mutationResult?.Data?.Id);
            }
            if (mutationResult) {
              const updatedSections = mutationResult?.Data?.Sections && [
                ...mutationResult?.Data?.Sections,
              ];
              let localSectionData;
              if (updatedSections && updatedSections.length > 0) {
                localSectionData = updatedSections.map((item: any, i: any) => ({
                  ...item,
                  localId: oldSections[i]?.Id,
                }));
              }
              const currentSectionItemIndex =
                localSectionData && localSectionData?.length > 0
                  ? localSectionData?.findIndex(
                      (item: any) => item?.localId === sectionItem?.Id,
                    )
                  : -1;
              if (currentSectionItemIndex >= 0) {
                callback &&
                  callback(updatedSections[currentSectionItemIndex]?.Id);
              }
              showSuccessToast({
                message: 'Your changes were successfully saved.',
                autoClose: 3000,
              });
            }
          },
          onError: (e: any) => {
            setIsAdd(true);
            showErrorToast({ message: e.Errors[0].Message, autoClose: 10000 });
          },
        },
      );
  }

  function handleSave(
    newSections?: Array<SectionTypeInput>,
    questionParams?: any,
    callback?: (isSuccess: boolean, newSectionId?: number) => void,
  ) {
    setIsAdd(false);
    const saveSections = formatDataToSave(newSections);

    if (formDetails === undefined) {
      saveSections.forEach((section: any) => {
        section.Questions.forEach((question: any) => {
          delete question.QuestionTemplate;
        });
      });
    }

    if (accountInfoReturn)
      saveForm(
        {
          AccountId: accountId,
          CID: accountInfoReturn?.Data.CID,
          Id: formDetails && formDetails.Data ? formDetails.Data.Id : 0,
          LastModified:
            formDetails && formDetails.Data
              ? formDetails.Data.LastModified
              : null,
          LastModifiedBy:
            formDetails && formDetails.Data
              ? formDetails.Data.LastModifiedBy
              : '',
          Notes: formNotes,
          PID: accountInfoReturn?.Data.PID,
          SID: accountInfoReturn?.Data.SID,
          Sections: saveSections,
          SurveyStatusId:
            formDetails && formDetails.Data
              ? formDetails.Data.SurveyStatusId
              : 1,
        },
        {
          onSuccess: (mutationResult: any) => {
            if (mutationResult?.Data?.Id) {
              switchTab && switchTab('EditForm', mutationResult?.Data?.Id);
            }
            if (mutationResult && newSections && questionParams) {
              const updatedSections = mutationResult?.Data?.Sections && [
                ...mutationResult?.Data.Sections,
              ];
              let localSectionData;
              if (
                updatedSections &&
                updatedSections.length > 0 &&
                newSections
              ) {
                localSectionData = updatedSections.map((item: any, i: any) => ({
                  ...item,
                  localId: newSections[i]?.Id,
                }));
              }

              const currentSectionItemIndex =
                localSectionData && localSectionData?.length > 0
                  ? localSectionData?.findIndex(
                      (item: any) => item?.localId === currentSection?.Id,
                    )
                  : -1;

              if (currentSectionItemIndex >= 0) {
                const currentSectionItem =
                  updatedSections[currentSectionItemIndex];
                const currentSectionQuestions = currentSectionItem?.Questions;
                const currentQuestionItem = currentSectionQuestions?.find(
                  (item: any) =>
                    item?.QuestionTemplateId ===
                    questionParams?.QuestionTemplateId,
                );
                const currentQuestionItemIndex =
                  currentSectionQuestions?.findIndex(
                    (item: any) =>
                      item?.QuestionTemplateId ===
                      questionParams?.QuestionTemplateId,
                  );

                if (currentSectionItem)
                  handleCurrentSection(currentSectionItem);
                if (currentQuestionItem) {
                  handleCurrentQuestion({
                    ...currentQuestionItem,
                    QuestionTemplate: {
                      ...newSections[currentSectionItemIndex].Questions[
                        currentQuestionItemIndex
                      ].QuestionTemplate,
                    },
                  });
                }
              }
            }
            callback && callback(true);

            showSuccessToast({
              message: 'Your changes were successfully saved.',
              autoClose: 3000,
            });
          },
          onError: (e: any) => {
            setIsAdd(true);
            showErrorToast({ message: e.Errors[0].Message, autoClose: 10000 });
            callback && callback(false);
          },
        },
      );
  }

  const handleCancel = () => {
    if (isAdd)
      confirmAlert({
        message:
          'Any unsaved changes will be lost, do you want to leave this page and discard your changes?',
        buttons: [
          {
            label: 'Yes',
            onClick: () => {
              setIsAdd(false, () => switchTab && switchTab('FormList'));
            },
            className: 'pr-btn-primary',
          },
          {
            label: 'No',
            onClick: () => {
              //
            },
          },
        ],
      });
    else {
      switchTab && switchTab('FormList');
    }
  };

  const handleMoveSectionToTop = (index: number) => {
    const allSections = [...sections];
    if (index === 0) return;
    allSections.splice(0, 0, allSections.splice(index, 1)[0]);
    setSections([...allSections]);
    setIsAdd(true);
  };

  const handleMoveSectionToBottom = (index: number) => {
    const allSections = [...sections];
    if (index === allSections.length - 1) return;
    allSections.splice(allSections.length, 0, allSections.splice(index, 1)[0]);
    setSections([...allSections]);
    setIsAdd(true);
  };

  const handleMoveSectionUp = (index: number) => {
    const allSections = [...sections];
    if (index === 0) return;
    allSections.splice(index - 1, 0, allSections.splice(index, 1)[0]);
    setSections([...allSections]);
    setIsAdd(true);
  };

  const handleMoveSectionDown = (index: number) => {
    const allSections = [...sections];
    if (index === allSections.length - 1) return;
    allSections.splice(index + 1, 0, allSections.splice(index, 1)[0]);
    setSections([...allSections]);
    setIsAdd(true);
  };

  const handleSectionDragAndDrop = (
    section: SectionTypeInput,
    index: number,
  ) => {
    const allSections = [...sections];
    allSections[index] = { ...section };
    setSections([...allSections]);
    setIsAdd(true);
  };

  function isPreviewEnabled(): boolean {
    if (!formDetails) return false;
    if (formDetails?.Data?.Notes !== formNotes) return false;
    if (
      JSON.stringify(formDetails?.Data?.Sections) !== JSON.stringify(sections)
    )
      return false;

    return true;
  }

  const formActions = [
    {
      action: 'Preview',
      icon: <Icon.Preview />,
      disabled: !isPreviewEnabled(),
      handleButtonClick: () => {
        window.open(
          `${process.env.REACT_APP_CALLCENTER_REDIRECT_URL}/Calls/Call/00000000-0000-0000-0000-000000000000?accountId=${accountId}&formId=${formId}`,
        );
      },
    },
    {
      action: 'Check Links',
      icon: <Icon.Link />,
      disabled: !formId,
      handleButtonClick: () =>
        window.open(
          `/home/forms/check-links/${accountNumber}?accountId=${accountId}&surveyId=${formId}`,
        ),
    },
    {
      action: 'Cancel',
      icon: <Icon.Cancel />,
      handleButtonClick: () => handleCancel(),
    },
    {
      action: 'Save',
      icon: <Icon.Check />,
      handleButtonClick: () => handleSave(),
    },
  ];

  const saveUnsavedChanges = () => {
    confirmAlert({
      message: 'Please save the form to continue with copy section.',
      buttons: [
        {
          label: 'Save',
          onClick: () => {
            handleSave(undefined, null, () => {
              setCopySection(true);
            });
          },
          className: 'pr-btn-primary',
        },
        {
          label: 'Cancel',
          onClick: () => {
            //
          },
        },
      ],
    });
  };

  const listActions = () => {
    const showCollapsed = Object.values(expandAll).find(item => item === true);
    return [
      {
        action: 'Add New Section',
        icon: <Icon.Add />,
        handleButtonClick: () => {
          setNewSection(true);
        },
      },
      {
        action: 'Copy Section',
        icon: <Icon.Copy />,
        handleButtonClick: () => {
          if (!match) {
            if (isAdd) {
              saveUnsavedChanges();
            } else {
              setCopySection(true);
            }
          } else {
            saveUnsavedChanges();
          }
        },
      },
      {
        action: 'Add Global Section',
        icon: <Icon.Add />,
        handleButtonClick: () => {
          setGlobalSection(true);
        },
      },
      {
        action: showCollapsed ? 'Collapse All' : 'Expand All',
        icon: !showCollapsed ? (
          <Icon.ChevronDown title="" />
        ) : (
          <Icon.ChevronUp title="" />
        ),
        handleButtonClick: () => {
          allSectionExpandCollapse();
        },
      },
    ];
  };

  function copyQuestion(selectedQuestion: Questions, cb: () => void) {
    if (formId)
      copyQuestionApi(
        {
          formId: formId,
          accountId: accountId,
          sectionId: showCopyQuestionPanel,
          questionId: selectedQuestion.Id,
        },
        {
          onSettled: cb,
        },
      );
  }

  function copySection(sections: Array<SectionTypeInput>, cb: () => void) {
    if (formId)
      copySectionApi(
        {
          id: formId,
          accountId: accountId,
          newSectionIds: sections.map(item => item.Id),
        },
        {
          onSuccess: cb,
          onError: (Errors: ErrorModal) => {
            showErrorToast({
              message: Errors.Errors[0].Message,
              autoClose: false,
            });
          },
        },
      );
  }

  const index =
    sections &&
    sections.findIndex(section => section.Id === showCopyQuestionPanel);
  let allQuestionsInSection: Array<Questions> = [];
  if (index >= 0) {
    const sectionItem = { ...sections[index] };
    allQuestionsInSection = [...sectionItem?.Questions];
  }

  return (
    <>
      <MainContent flexDirection="column" m={-4} {...StyleProps}>
        <PageHeader
          title={`${!formId ? 'Add New Form' : 'Edit Form'}`}
          actions={formActions}
          hasBackNav
          backNavAction={handleCancel}
          backNavTooltip="Go back to Forms List"
        />
        <Flex
          p={4}
          sx={{
            background: 'var(--color-neutral-contrast-00)',
            '& > * + *': { marginLeft: '1.2rem' },
          }}
        >
          <FormInput
            style={{ flex: 1 }}
            label="Account Number"
            disabled
            value={accountNumber}
          />
          <FormInput
            style={{ flex: 1 }}
            label="Account Name"
            disabled
            value={accountInfoReturn?.Data.Name}
          />
          <FormInput
            style={{ flex: 2 }}
            label="Form Notes"
            value={formNotes}
            onTextChange={(text: string) => {
              setFormNotes(text);
              setIsAdd(true);
            }}
          />
        </Flex>
        <PageHeader title="List of Sections" actions={listActions()} />
        <SectionList
          isAdd={isAdd}
          toggleQuestionPanel={toggleQuestionPanel}
          toggleNewSection={toggleNewSection}
          sections={sections}
          handleCurrentSection={handleCurrentSection}
          handleDeleteSection={handleDeleteSection}
          handleMoveSectionToTop={handleMoveSectionToTop}
          handleMoveSectionToBottom={handleMoveSectionToBottom}
          handleMoveSectionUp={handleMoveSectionUp}
          handleMoveSectionDown={handleMoveSectionDown}
          handleCurrentQuestion={handleCurrentQuestion}
          handleDeleteQuestion={handleDeleteQuestion}
          toggleCopyQuestionPanel={toggleCopyQuestionPanel}
          handleSectionDragAndDrop={handleSectionDragAndDrop}
          openCloseSection={openCloseSection}
          expandedState={expandAll}
          isCopyQnEnabled={isPreviewEnabled()}
          handleSaveForm={handleSaveForm}
        />
      </MainContent>

      <AddEditSection
        showPanel={showNewSection}
        togglePanel={toggleNewSection}
        handleAddEditSections={handleAddEditSections}
        currentSection={currentSection}
        accountInfoReturn={accountInfoReturn?.Data}
        isNewForm={match}
      />

      <AddGlobalSection
        showPanel={showGlobalSection}
        togglePanel={toggleGlobalSection}
        handleAddEditGlobalSection={handleAddEditGlobalSection}
      />

      <AddEditQuestion
        showPanel={showQuestionPanel}
        togglePanel={toggleQuestionPanel}
        handleAddEditQuestion={handleAddEditQuestion}
        currentSection={currentSection}
        selectedQuestionType={selectedQuestionType}
        setQuestionType={setQuestionType}
        currentQuestion={currentQuestion}
        accountId={accountId}
        accountInfoReturn={accountInfoReturn?.Data}
        sections={sections}
        handleSaveForm={handleSave}
        url={url}
        setUrl={setUrl}
      />

      <CopySection
        showPanel={showCopySection}
        togglePanel={toggleCopySectionPanel}
        copySection={copySection}
        isCopyQuestion={false}
      />

      <CopySection
        showPanel={showCopyQuestionPanel !== 0}
        togglePanel={toggleCopyQuestionPanel}
        copyQuestion={copyQuestion}
        isCopyQuestion={true}
        allQuestionsInSection={allQuestionsInSection}
      />

      {isLoading && <Loader />}
    </>
  );
}
